import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaLock, FaClock, FaShieldAlt, FaUsers } from 'react-icons/fa';
import './TokenomicsCard.scss';
import {
    buildJustLockSnapshot,
    fetchJustLockLocks,
    fetchJustLockResource,
} from '../../../utils/justLock';

const REDDICKS_RESOURCE_ADDRESS = 'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const TOTAL_SUPPLY = 1000000000;
const FALLBACK_FOREVER_LOCKED = 402296722;
const FALLBACK_TOTAL_LOCKED = 549464187.7336735;

const TokenomicsCard = () => {
    const [lockData, setLockData] = useState(null);
    const [lockDetails, setLockDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const fetchLock = async () => {
            try {
                setLoading(true);
                const data = await fetchJustLockResource(REDDICKS_RESOURCE_ADDRESS);
                setLockData(data);

                const liveLockIds = Array.isArray(data?.lock_summary?.lock_ids)
                    ? data.lock_summary.lock_ids
                    : [];

                if (liveLockIds.length) {
                    try {
                        setLockDetails(await fetchJustLockLocks(liveLockIds));
                    } catch (detailErr) {
                        console.warn('TokenomicsCard detail fetch warning:', detailErr);
                        setLockDetails([]);
                    }
                } else {
                    setLockDetails([]);
                }

                setLoading(false);
            } catch (err) {
                console.error('TokenomicsCard error:', err);
                setError(err.message);
                setLoading(false);
            }
        };
        fetchLock();
    }, []);

    // Fixed-delay mount animation (synced with HomepageCard)
    useEffect(() => {
        const t = setTimeout(() => setIsVisible(true), 800);
        return () => clearTimeout(t);
    }, []);

    const lockSnapshot = useMemo(
        () =>
            buildJustLockSnapshot({
                resourceData: lockData,
                lockDetails,
                resourceAddress: REDDICKS_RESOURCE_ADDRESS,
                totalSupply: TOTAL_SUPPLY,
                fallbackTotalLocked: FALLBACK_TOTAL_LOCKED,
                fallbackForeverLocked: FALLBACK_FOREVER_LOCKED,
            }),
        [lockData, lockDetails]
    );

    const lockedAmount = lockSnapshot.totalLocked;
    const totalLockedPct = (lockedAmount / TOTAL_SUPPLY) * 100;
    const foreverLockedPct = (lockSnapshot.foreverLocked / TOTAL_SUPPLY) * 100;
    const timeLockedTokens = lockSnapshot.timeLocked;
    const timeLockedPct = (timeLockedTokens / TOTAL_SUPPLY) * 100;
    const circulatingPct = Math.max(0, 100 - totalLockedPct);

    const segments = [
        { label: 'Forever Locked', pct: foreverLockedPct, color: '#FF3B5F', icon: <FaLock /> },
        { label: 'Time-Locked', pct: timeLockedPct, color: '#00A3FF', icon: <FaClock /> },
        { label: 'Circulating', pct: circulatingPct, color: '#00FF9D', icon: <FaUsers /> },
    ];

    return (
        <div className={`tokenomics-card ${isVisible ? 'visible' : ''} ${loading ? 'loading' : ''} ${error ? 'error' : ''}`}>
            <div className="tkc-glow"></div>
            <div className="tkc-accent-line"></div>

            <div className="tkc-inner">
                {loading && <div className="tkc-loading"><div className="tkc-spinner"></div></div>}
                {error && <div className="tkc-error">Error: {error}</div>}
                {!loading && !error && lockData && (
                    <>
                        {/* Header */}
                        <div className="tkc-header">
                            <FaShieldAlt className="tkc-header-icon" />
                            <div className="tkc-header-text">
                                <span className="tkc-symbol">Tokenomics</span>
                                <span className="tkc-label">Rug Proof POL</span>
                            </div>
                            <div className="tkc-live-dot"></div>
                        </div>

                        {/* Hero lock stat */}
                        <div className="tkc-lock-hero">
                            <span className="tkc-lock-value">{totalLockedPct.toFixed(1)}%</span>
                            <span className="tkc-lock-label">Total Supply Locked</span>
                        </div>

                        {/* Visual bar */}
                        <div className="tkc-bar">
                            {segments.map((seg) => (
                                <div
                                    key={seg.label}
                                    className="tkc-bar__segment"
                                    style={{ width: `${seg.pct}%`, backgroundColor: seg.color }}
                                    title={`${seg.label}: ${seg.pct.toFixed(1)}%`}
                                />
                            ))}
                        </div>

                        {/* Segment stats */}
                        <div className="tkc-segments">
                            {segments.map((seg) => (
                                <div className="tkc-seg" key={seg.label}>
                                    <span className="tkc-seg__dot" style={{ background: seg.color }}></span>
                                    <span className="tkc-seg__label">{seg.label}</span>
                                    <span className="tkc-seg__pct">{seg.pct.toFixed(1)}%</span>
                                </div>
                            ))}
                        </div>

                        {/* Trust line */}
                        <div className="tkc-trust-line">
                            <FaLock className="tkc-trust-icon" />
                            <span>Treasury-owned LP. 1% fees fund growth.</span>
                        </div>

                        {/* Footer */}
                        <div className="tkc-footer">
                            <Link to="/tokenomics" className="tkc-cta">
                                View Full Tokenomics <FaArrowRight />
                            </Link>
                            <Link to="/verify" className="tkc-cta tkc-cta--secondary">
                                Verify Locks
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TokenomicsCard;
